<template>
  <dialog ref="dialogRef" class="modal">
    <div class="modal-box w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div class="flex items-center gap-1 mb-4">
        <h3 class="font-bold text-lg">Détails de la dérogation</h3>
        <HelpButton />
      </div>

      <div v-if="derogation" class="space-y-4">
        <!-- Info générales -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm font-semibold">Demandeur</label>
            <p class="text-base-content/80">
              {{ derogation.requester?.firstName }} {{ derogation.requester?.lastName }}
            </p>
          </div>
          <div>
            <label class="text-sm font-semibold">Devis</label>
            <p class="text-base-content/80">{{ derogation.order?.quote?.quoteReference || '-' }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Client</label>
            <p class="text-base-content/80">{{ derogation.order?.quote?.company?.name || '-' }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Type</label>
            <p class="text-base-content/80">{{ getDerogationTypeLabel(derogation.type) }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Statut</label>
            <div class="mt-1">
              <span class="badge badge-ghost badge-sm">{{ derogation.status }}</span>
              <DerogationBadgeGrid :params="derogation.status" />
            </div>
          </div>
          <div>
            <label class="text-sm font-semibold">Date création</label>
            <p class="text-base-content/80">{{ formatDate(derogation.createdAt) }}</p>
          </div>
        </div>

        <!-- Description/Raison -->
        <div class="divider my-2"></div>
        <div class="space-y-3">
          <div>
            <label class="text-sm font-semibold block mb-2">Raison de la demande</label>
            <div class="p-3 bg-base-200 rounded border-l-4 border-primary">
              <template v-if="reasonObject">
                <div v-if="reasonObject.description" class="mb-3">
                  <p class="font-semibold">Description</p>
                  <p class="text-base-content/80">{{ reasonObject.description }}</p>
                </div>
                <div v-if="reasonObject.commercialArgument">
                  <p class="font-semibold">Argument commercial</p>
                  <p class="text-base-content/80">{{ reasonObject.commercialArgument }}</p>
                </div>
                <div v-if="reasonObject.raw && !reasonObject.description && !reasonObject.commercialArgument">
                  <p class="text-base-content/80">{{ reasonObject.raw }}</p>
                </div>
              </template>
              <p v-else class="text-base-content/80">Aucune raison spécifiée</p>
            </div>
          </div>
        </div>

        <!-- Commentaires -->
        <div class="divider my-2"></div>
        <div class="space-y-3">
          <label class="text-sm font-semibold block">Commentaires</label>
          <div ref="commentBoxRef" v-if="localComment" class="p-3 pr-4 bg-base-200 rounded whitespace-pre-wrap text-sm text-base-content/80 max-h-64 overflow-y-auto scrollbar-thin">{{ localComment }}</div>
          <p v-else class="text-sm text-base-content/50 italic">Aucun commentaire.</p>
          <div class="mt-2">
            <textarea
              v-model="newComment"
              class="textarea textarea-bordered w-full"
              rows="3"
              placeholder="Ajouter un commentaire..."
            ></textarea>
            <div class="flex justify-end mt-1">
              <button
                class="btn btn-sm btn-primary"
                :disabled="!newComment.trim() || isAddingComment"
                @click="handleAddComment"
              >
                <span v-if="isAddingComment" class="loading loading-spinner loading-xs"></span>
                Ajouter
              </button>
            </div>
          </div>
        </div>

        <!-- Section approbation (si manager et status PENDING) -->
        <div v-if="isManager && derogation.status === 'PENDING'" class="divider my-2"></div>
        <div v-if="(isDg || (isDv && derogation.level === 'DV')) && derogation.status === 'PENDING'" class="space-y-3">
          <label class="text-sm font-semibold">Commentaire (obligatoire)</label>
          <textarea
            v-model="comment"
            class="textarea textarea-bordered w-full"
            placeholder="Entrez votre commentaire..."
            rows="3"
          ></textarea>

          <div class="flex gap-2 justify-end pt-2">
            <button
              class="btn btn-error btn-sm"
              @click="handleReject"
              :disabled="!comment.trim() || isProcessing"
            >
              <span v-if="isProcessing" class="loading loading-spinner loading-xs"></span>
              Refuser
            </button>
            <button
              class="btn btn-success btn-sm"
              @click="handleApprove"
              :disabled="!comment.trim() || isProcessing"
            >
              <span v-if="isProcessing" class="loading loading-spinner loading-xs"></span>
              Approuver
            </button>
          </div>
        </div>

        <!-- Section info si non-manager ou status non-PENDING -->
        <div v-else-if="derogation.status !== 'PENDING'" class="divider my-2"></div>
        <div v-if="derogation.status !== 'PENDING'" class="space-y-2">
          <label class="text-sm font-semibold">Commentaire de décision</label>
          <p class="text-base-content/80 p-3 bg-base-200 rounded">
            {{ derogation.decisionComment || 'Aucun commentaire' }}
          </p>
          <div v-if="derogation.validator" class="text-sm text-base-content/60">
            Décision prise par: {{ derogation.validator?.firstName }} {{ derogation.validator?.lastName }}
            <br />
            le {{ formatDate(derogation.validatedAt) }}
          </div>
        </div>
      </div>

      <!-- Close button -->
      <div class="modal-action">
        <button class="btn" @click="close">Fermer</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="close">close</button>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useOrderDerogationStore } from '@/stores/useOrderDerogationStore'
import { formatDate } from '@/utils/dateUtils'
import { useToast } from 'vue-toast-notification'
import DerogationBadgeGrid from '@/components/data-visualization/grids/DerogationBadgeGrid.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import { useContextualHelp } from '@/composables/useContextualHelp'

interface Props {
  derogation?: any
  isOpen: boolean
  onDerogationUpdated?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
})

const emit = defineEmits<{
  close: []
  updated: []
}>()

const boostCoreStore = useBoostCorePermissionStore()
const orderDerogationStore = useOrderDerogationStore()
const $toast = useToast()

// Aide contextuelle (USER_MASTER) — active uniquement quand la modale est
// ouverte ; surcharge l'aide de la page puis la restaure à la fermeture.
useContextualHelp('derogation-detail', () => props.isOpen)

const dialogRef = ref<HTMLDialogElement | null>(null)
const comment = ref('')
const isProcessing = ref(false)
const newComment = ref('')
const isAddingComment = ref(false)
const localComment = ref<string | null>(null)
const commentBoxRef = ref<HTMLDivElement | null>(null)

function scrollCommentToBottom() {
  nextTick(() => {
    if (commentBoxRef.value) {
      commentBoxRef.value.scrollTop = commentBoxRef.value.scrollHeight
    }
  })
}

watch(
  () => props.derogation?.comment,
  (val) => {
    localComment.value = val ?? null
    scrollCommentToBottom()
  },
  { immediate: true },

)

watch(localComment, scrollCommentToBottom)

const isDv = computed(() => boostCoreStore.include('DEROGATION_DV'))
const isDg = computed(() => boostCoreStore.include('DEROGATION_DG') || boostCoreStore.include('SUPER_ADMIN'))

interface ReasonDetails {
  description?: string
  commercialArgument?: string
  raw?: string
}

const isManager = computed(() => boostCoreStore.isManager)

const reasonObject = computed<ReasonDetails | null>(() => {
  const reason = props.derogation?.reason
  if (!reason) {
    return null
  }

  if (typeof reason === 'object' && reason !== null) {
    return {
      description: reason.description,
      commercialArgument: reason.commercialArgument,
    }
  }

  if (typeof reason === 'string') {
    try {
      const parsed = JSON.parse(reason)
      if (parsed && typeof parsed === 'object') {
        return {
          description: parsed.description,
          commercialArgument: parsed.commercialArgument,
          raw: typeof parsed === 'string' ? parsed : undefined,
        }
      }
    } catch {
      return { raw: reason }
    }
  }

  return null
})

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      dialogRef.value?.showModal()
    } else {
      dialogRef.value?.close()
      comment.value = ''
      newComment.value = ''
    }
  },
)

function getDerogationTypeLabel(type: string): string {
  const typeLabels: Record<string, string> = {
    MARGIN: 'Marge faible',
    PAYMENT_MODE: 'Mode de paiement',
    MANUAL_SIGNATURE: 'Signature manuelle',
    EXCEPTIONAL_DISCOUNT: 'Remise exceptionnelle',
    MISSING_DOCUMENT: 'Document manquant',
    MARCHE_PUBLIC: 'Marché public',
  }

  return typeLabels[type] || type
}

async function handleAddComment() {
  if (!newComment.value.trim()) return
  isAddingComment.value = true
  try {
    const updated = await orderDerogationStore.addDerogationComment(props.derogation.uuid, newComment.value)
    if (updated?.comment !== undefined) {
      localComment.value = updated.comment
    }
    newComment.value = ''
    $toast.success('Commentaire ajouté')
  } catch (error) {
    $toast.error('Erreur lors de l\'ajout du commentaire')
    console.error(error)
  } finally {
    isAddingComment.value = false
  }
}

async function handleApprove() {
  if (!comment.value.trim()) {
    $toast.error('Le commentaire est obligatoire')
    return
  }

  isProcessing.value = true
  try {
    await orderDerogationStore.updateDerogationStatus(props.derogation.uuid, 'APPROVED', comment.value)
    $toast.success('Dérogation approuvée')
    props.onDerogationUpdated?.()
    emit('updated')
    close()
  } catch (error) {
    $toast.error('Erreur lors de l\'approbation')
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

async function handleReject() {
  if (!comment.value.trim()) {
    $toast.error('Le commentaire est obligatoire')
    return
  }

  isProcessing.value = true
  try {
    await orderDerogationStore.updateDerogationStatus(props.derogation.uuid, 'REJECTED', comment.value)
    $toast.success('Dérogation rejetée')
    props.onDerogationUpdated?.()
    emit('updated')
    close()
  } catch (error) {
    $toast.error('Erreur lors du rejet')
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

function close() {
  dialogRef.value?.close()
  comment.value = ''
  emit('close')
}
</script>
